/**
 * HomeSections — renders all homepage sections with the given theme context.
 * Each section receives: content data + theme style overrides via the `styles` prop.
 */
import { useState } from 'react';
import { usePage, Link } from '@inertiajs/react';

/* ── Image Card (used by Image Studio) — 3D flip ───────── */
export function ImageCard({ item, cardClass = '' }) {
    return (
        <div
            className={`aspect-[3/4] cursor-pointer ${cardClass}`}
            style={{ perspective: '1200px' }}
            onMouseEnter={e => e.currentTarget.firstChild.style.transform = 'rotateY(180deg)'}
            onMouseLeave={e => e.currentTarget.firstChild.style.transform = 'rotateY(0deg)'}
        >
            <div
                className="relative w-full h-full transition-transform duration-700 ease-in-out"
                style={{ transformStyle: 'preserve-3d' }}
            >
                {/* ── FRONT ── */}
                <div
                    className="absolute inset-0 rounded-2xl overflow-hidden border-2 border-slate-200 dark:border-white/5"
                    style={{ backfaceVisibility: 'hidden' }}
                >
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${item.main}')` }} />
                    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 bg-primary/70 px-4 py-4">
                        <h3 className="text-white font-black text-sm tracking-widest uppercase leading-tight">{item.title}</h3>
                        <span className="material-symbols-outlined text-white text-[18px] flex-shrink-0">open_in_new</span>
                    </div>
                </div>

                {/* ── BACK ── */}
                <div
                    className="absolute inset-0 rounded-2xl overflow-hidden border-2 border-primary/60"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                >
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${item.main}')` }} />
                    <div className="absolute inset-0 bg-primary/80" />
                    <div className="relative z-10 h-full flex flex-col items-center justify-center gap-6 p-5">
                        <div className="flex items-center gap-5">
                            <div
                                className="size-24 sm:size-28 rounded-xl bg-cover bg-center border-2 border-white/40 shadow-2xl"
                                style={{ backgroundImage: `url('${item.sub1}')` }}
                            />
                            <span className="text-white font-black text-4xl leading-none drop-shadow-lg">+</span>
                            <div
                                className="size-24 sm:size-28 rounded-xl bg-cover bg-center border-2 border-white/40 shadow-2xl"
                                style={{ backgroundImage: `url('${item.sub2}')` }}
                            />
                        </div>
                        <p className="text-white text-sm font-black uppercase tracking-widest text-center">{item.title}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ── Video Card ─────────────────────────────────────────────── */
export function VideoCard({ item, cardClass = '' }) {
    const [active, setActive] = useState(false);
    const stopPreview = (node) => {
        const vid = node?.querySelector('video');
        if (vid) {
            vid.pause();
            vid.currentTime = 0;
        }
        setActive(false);
    };

    return (
        <div
            className={`aspect-[3/4] relative rounded-2xl overflow-hidden border-2 hover:border-primary/40 hover:border-4 transition-all group cursor-pointer duration-300 ${cardClass}`}
            onMouseEnter={() => setActive(true)}
            onMouseLeave={(e) => stopPreview(e.currentTarget)}
            onFocus={() => setActive(true)}
            onBlur={(e) => stopPreview(e.currentTarget)}
        >
            {active && item.video && (
                <video
                    src={item.video}
                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    loop
                    muted
                    playsInline
                    preload="none"
                    ref={(node) => node?.play?.()?.catch?.(() => {})}
                />
            )}
            <div className="absolute inset-0 bg-cover bg-center group-hover:opacity-0 transition-opacity duration-500" style={{ backgroundImage: `url('${item.thumbnail}')` }} />
            <div className="absolute inset-0 bg-white/20 dark:bg-black/40 group-hover:opacity-0 transition-opacity duration-300" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 group-hover:opacity-0 transition-opacity duration-300 p-4 pointer-events-none">
                <div className="size-16 rounded-full flex items-center justify-center bg-white/80 dark:bg-black/50 backdrop-blur-md border border-slate-200 dark:border-white/5 text-primary shadow-xl">
                    <span className="material-symbols-outlined text-4xl fill-1 ml-1">play_arrow</span>
                </div>
                <h3 className="text-2xl font-bold text-primary tracking-widest uppercase text-center drop-shadow-md">{item.title}</h3>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center gap-3 bg-white/90 dark:bg-black/70 backdrop-blur-md p-3 rounded-xl border border-slate-200 dark:border-white/5 translate-y-12 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-75 pointer-events-none shadow-lg">
                <div className="size-14 rounded-lg bg-cover bg-center border border-slate-200 dark:border-white/5" style={{ backgroundImage: `url('${item.sub1}')` }} />
                <span className="material-symbols-outlined text-slate-600 dark:text-white/60 text-xl">add</span>
                <div className="size-14 rounded-lg bg-cover bg-center border border-slate-200 dark:border-white/5" style={{ backgroundImage: `url('${item.sub2}')` }} />
            </div>
        </div>
    );
}

/* ── Chat Demo ──────────────────────────────────────────────── */
export function ChatDemo({ panelClass = '' }) {
    return (
        <div className={`relative p-8 rounded-3xl border-2 shadow-2xl ${panelClass}`}>
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                    <div className="size-10 rounded-full bg-primary flex items-center justify-center text-black font-bold shrink-0">AI</div>
                    <div className="flex-1 bg-slate-100 dark:bg-white/5 p-4 rounded-2xl rounded-tl-none border border-slate-200 dark:border-white/5">
                        <p className="text-slate-800 dark:text-white/90 text-sm">Hello! I'm your AI creative assistant. How can I help you bring your ideas to life today?</p>
                    </div>
                </div>
                <div className="flex items-start gap-3 justify-end">
                    <div className="bg-primary/20 p-4 rounded-2xl rounded-tr-none border border-primary/30 max-w-[80%]">
                        <p className="text-slate-900 dark:text-white/90 text-sm">I need help designing a futuristic logo for my tech startup.</p>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <div className="size-10 rounded-full bg-primary flex items-center justify-center text-black font-bold shrink-0">AI</div>
                    <div className="flex-1 bg-slate-100 dark:bg-white/5 p-4 rounded-2xl rounded-tl-none border border-slate-200 dark:border-white/5">
                        <p className="text-slate-800 dark:text-white/90 text-sm">Great! Let's create something amazing. What's your startup's name and core values?</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                    <input readOnly placeholder="Type a message..." className="flex-1 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/5 rounded-full px-4 py-2 text-sm text-slate-400 dark:text-white/30 outline-none" />
                    <div className="size-9 bg-primary rounded-full flex items-center justify-center"><span className="material-symbols-outlined text-black text-[18px]">send</span></div>
                </div>
            </div>
        </div>
    );
}

/* ── Voice Demo ─────────────────────────────────────────────── */
export function VoiceDemo({ panelClass = '' }) {
    return (
        <div className={`relative p-10 rounded-3xl border-2 shadow-2xl ${panelClass}`}>
            <div className="text-center space-y-6">
                <div className="flex justify-center items-end gap-2 h-20">
                    {[...Array(12)].map((_, i) => (
                        <div key={i} className="w-2 bg-primary rounded-full animate-pulse"
                            style={{ height: `${[35, 55, 70, 45, 80, 60, 40, 72, 50, 65, 38, 58][i]}%`, animationDelay: `${i * 0.1}s` }} />
                    ))}
                </div>
                <div className="text-slate-500 dark:text-white/50 text-sm">Neural voice synthesis in action...</div>
                <div className="size-20 mx-auto bg-primary rounded-full flex items-center justify-center text-black shadow-lg shadow-primary/40">
                    <span className="material-symbols-outlined text-4xl">mic</span>
                </div>
                <div className="flex justify-center gap-4 text-xs text-slate-400 dark:text-white/30">
                    <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5">100+ Languages</span>
                    <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5">Voice Cloning</span>
                    <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5">HD Quality</span>
                </div>
            </div>
        </div>
    );
}

/* ── Section Badge ──────────────────────────────────────────── */
export function SectionBadge({ icon, text }) {
    return (
        <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/20 border border-primary rounded-full text-primary text-sm font-bold uppercase tracking-wider">
            <span className="material-symbols-outlined text-lg">{icon}</span>
            {text}
        </div>
    );
}

/* ── Feature List ───────────────────────────────────────────── */
export function FeatureList({ items, textClass = 'text-slate-700 dark:text-white/80' }) {
    return (
        <ul className="space-y-4">
            {items.map((f, i) => (
                <li key={i} className={`flex items-center gap-3 ${textClass}`}>
                    <span className="material-symbols-outlined text-primary text-2xl shrink-0">check_circle</span>
                    <span className="text-lg font-medium">{f}</span>
                </li>
            ))}
        </ul>
    );
}

/* ── Quick Actions Grid ──────────────────────────────────────────── */
export function HomeQuickActions() {
    const { page } = usePage().props;
    const c = page?.content || {};
    const items = c.quick_actions || [];

    if (!items || items.length === 0) return null;
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((action, idx) => (
                <a
                    key={idx}
                    href={action.url || '#'}
                    className="relative group overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-black/20 backdrop-blur-md p-5 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
                >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-br transition-opacity duration-300 from-primary to-purple-500"></div>
                    <div className="relative z-10 flex flex-col items-center gap-3 text-center">
                        <div className="size-16 rounded-xl flex items-center justify-center bg-slate-100 dark:bg-white/5 text-white shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden border border-slate-200 dark:border-white/10">
                            {action.image ? (
                                <img src={action.image} alt={action.label} className="w-full h-full object-cover" />
                            ) : (
                                <span className="material-symbols-outlined text-2xl text-slate-400">bolt</span>
                            )}
                        </div>
                        <h4 className="font-bold text-slate-800 dark:text-white/90 text-[13px] tracking-wide group-hover:text-primary transition-colors">{action.label}</h4>
                    </div>
                    {/* Corner glow */}
                    <div className="absolute -bottom-8 -right-8 size-24 bg-gradient-to-br from-primary to-purple-500 rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                </a>
            ))}
        </div>
    );
}

/* ── Image Trends Grid ──────────────────────────────────────────── */
export function HomeImageTrends() {
    const { trendAlbums } = usePage().props;

    if (!trendAlbums || trendAlbums.length === 0) return null;

    return (
        <div className="space-y-10">
            <div className="grid gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {trendAlbums.map((album) => (
                    <Link
                        key={album.id}
                        href={route('image-trends.show', album.slug)}
                        className="group cursor-pointer bg-slate-50 dark:bg-white/5 rounded-2xl transition-all hover:bg-slate-100 dark:hover:bg-white/10 flex flex-col border border-slate-200 dark:border-white/10 overflow-hidden hover:-translate-y-1 hover:shadow-xl duration-300"
                    >
                        {/* Cover Image */}
                        <div className="relative overflow-hidden bg-slate-200 dark:bg-[#2a2a2a] shrink-0 w-full aspect-[4/5]">
                            <div
                                className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                style={{ backgroundImage: album.cover_image ? `url('${album.cover_image}')` : undefined }}
                            >
                                {!album.cover_image && (
                                    <div className="w-full h-full bg-gradient-to-br from-primary/20 via-purple-500/10 to-blue-500/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-slate-300 dark:text-white/20 text-[80px]">photo_library</span>
                                    </div>
                                )}
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        </div>

                        {/* Info */}
                        <div className="flex flex-1 justify-between items-center px-5 py-4">
                            <div>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                                    {album.name}
                                </h3>
                                <p className="text-slate-500 dark:text-[#a1a1a1] text-sm mt-0.5">
                                    {album.templates_count || 0} Templates
                                </p>
                            </div>
                            <span className="material-symbols-outlined text-slate-400 dark:text-white/30 group-hover:text-primary transition-colors text-xl">arrow_forward</span>
                        </div>
                    </Link>
                ))}
            </div>
            <div className="flex justify-center">
                <Link
                    href={route('image-trends.index')}
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-bold text-sm uppercase tracking-wider transition-all hover:scale-105 duration-300"
                >
                    <span>Read More</span>
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </Link>
            </div>
        </div>
    );
}

            /* Video Trends Grid */
export function HomeVideoTrends() {
    const { avatarTemplates } = usePage().props;
    const templates = avatarTemplates || [];
    const [activeKey, setActiveKey] = useState(null);

    if (!templates || templates.length === 0) return null;

    return (
        <div className="space-y-10">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                {templates.map((template, idx) => {
                    const key = template.id || idx;
                    const isActive = activeKey === key;
                    const hasVideo = !!template.reference_video_url;
                    const shouldPreviewVideo = isActive && hasVideo;
                    const previewUrl = template.resolved_preview_image || template.preview_image || '';

                    return (
                        <Link
                            key={key}
                            href={route('avatar-template.index')}
                            onMouseEnter={() => setActiveKey(key)}
                            onMouseLeave={() => setActiveKey(null)}
                            onFocus={() => setActiveKey(key)}
                            onBlur={() => setActiveKey(null)}
                            className="group overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                        >
                            <div className="relative aspect-[3/4] overflow-hidden bg-slate-200 dark:bg-[#2a2a2a]">
                                {shouldPreviewVideo && (
                                    <video
                                        key={`${key}-${template.reference_video_url}`}
                                        ref={(node) => node?.play?.()?.catch?.(() => {})}
                                        src={template.reference_video_url}
                                        className="absolute inset-0 z-10 h-full w-full object-cover"
                                        loop
                                        muted
                                        playsInline
                                        preload="none"
                                    />
                                )}

                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt={template.name}
                                        className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${shouldPreviewVideo ? 'opacity-0' : 'opacity-100'}`}
                                    />
                                ) : (
                                    <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 via-emerald-500/10 to-cyan-500/10 transition-opacity duration-500 ${shouldPreviewVideo ? 'opacity-0' : 'opacity-100'}`}>
                                        <span className="material-symbols-outlined text-[60px] text-slate-300 dark:text-white/20">movie</span>
                                    </div>
                                )}

                                <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-90" />
                                {hasVideo && (
                                    <div className="absolute left-3 top-3 z-30 flex size-10 items-center justify-center rounded-full bg-white/20 text-white shadow-lg backdrop-blur-sm transition-transform group-hover:scale-110">
                                        <span className="material-symbols-outlined text-2xl">play_arrow</span>
                                    </div>
                                )}
                                <div className="absolute inset-x-0 bottom-0 z-30 p-4">
                                    <h3 className="line-clamp-2 text-base font-bold leading-tight text-white transition-colors group-hover:text-primary">
                                        {template.name}
                                    </h3>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
            <div className="flex justify-center">
                <Link
                    href={route('avatar-template.index')}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-primary transition-all duration-300 hover:scale-105 hover:bg-primary/10"
                >
                    <span>View Templates</span>
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </Link>
            </div>
        </div>
    );
}
